import { useEffect } from "react";

import NavLogFilterBar from "components/control/NavLogFilterBar";
import NavLogFilterModeToggle from "components/control/NavLogFilterModeToggle";
import { navServiceLogLineClassName } from "lib/utils/navServiceLogLineStyle";
import { useDevNavLogs, useDevNavLogsScroll } from "providers/DevNavLogsProvider";

const formatTwistAxis = (value: number) => value.toFixed(3);

const DeveloperNavLogsPanel: React.FC = () => {
    const {
        popupEnabled,
        setPopupEnabled,
        registerSettingsPanel,
        logFilterDraft,
        setLogFilterDraft,
        logFilterItems,
        addLogFilterItem,
        removeLogFilterItem,
        logFilterMode,
        setLogFilterMode,
        paused,
        loading,
        error,
        togglePaused,
        performMovement,
    } = useDevNavLogs();
    const { scrollRef, onScroll, clear, lines } = useDevNavLogsScroll();

    useEffect(() => registerSettingsPanel(), [registerSettingsPanel]);

    return (
        <section className="mt-6 max-w-5xl">
            <h3 className="text-h5 font-semibold text-primary2">nav.service logs</h3>

            <div className="mt-4 flex items-start gap-4">
                <div className="min-w-0 flex-1">
                    {error ? <p className="text-h5 text-warning">{error}</p> : null}

                    <div className="mt-3 flex flex-wrap items-start gap-2">
                        <NavLogFilterBar
                            draft={logFilterDraft}
                            onDraftChange={setLogFilterDraft}
                            items={logFilterItems}
                            onAddItem={addLogFilterItem}
                            onRemoveItem={removeLogFilterItem}
                            mode={logFilterMode}
                        />
                        <NavLogFilterModeToggle
                            mode={logFilterMode}
                            onChange={setLogFilterMode}
                        />
                        <button
                            type="button"
                            onClick={togglePaused}
                            className="rounded-r5 border border-black70 bg-black90 px-3 py-1 text-h5 text-white hover:bg-black80"
                        >
                            {paused ? "Resume" : "Pause"}
                        </button>
                        <button
                            type="button"
                            onClick={clear}
                            className="rounded-r5 border border-black70 bg-black90 px-3 py-1 text-h5 text-white hover:bg-black80"
                        >
                            Clear
                        </button>
                    </div>

                    <div className="mt-3 rounded-r10 border border-black80 bg-black100 px-3 py-2 font-mono text-[12px] tabular-nums text-black40">
                        <span className="text-black60">/perform/movement</span>
                        {performMovement ? (
                            <span className="ml-2 text-white">
                                vx={formatTwistAxis(performMovement.linearX)}{" "}
                                vy={formatTwistAxis(performMovement.linearY)}{" "}
                                wz={formatTwistAxis(performMovement.angularZ)}{" "}
                                {performMovement.hz.toFixed(1)} Hz
                            </span>
                        ) : (
                            <span className="ml-2">waiting…</span>
                        )}
                    </div>

                    <div
                        ref={scrollRef}
                        onScroll={onScroll}
                        className="mt-3 h-[min(24rem,40vh)] overflow-y-auto rounded-r10 border border-black80 bg-black100 p-3 font-mono text-[12px] leading-relaxed text-white"
                    >
                        {lines.length === 0 ? (
                            <p className="text-black40">
                                {loading
                                    ? "Loading recent logs…"
                                    : logFilterItems.length > 0
                                      ? "No matching log lines."
                                      : "No log lines yet."}
                            </p>
                        ) : (
                            lines.map((line, index) => (
                                <div
                                    key={`${index}-${line.slice(0, 32)}`}
                                    className={`whitespace-pre-wrap break-words${navServiceLogLineClassName(line)}`}
                                >
                                    {line}
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="w-44 shrink-0 rounded-r10 border border-black80 bg-black100 px-3 py-3">
                    <p className="text-h5 font-semibold text-white">Control page popup</p>
                    <p className="mt-1 text-h5 text-black40">
                        Floating nav.service log console on the control screen.
                    </p>
                    <div className="mt-4 flex items-center justify-between gap-2">
                        <span className="text-h5 tabular-nums text-black40">
                            {popupEnabled ? "On" : "Off"}
                        </span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={popupEnabled}
                            aria-label="Enable nav.service log popup on control page"
                            onClick={() => setPopupEnabled(!popupEnabled)}
                            className={`inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full border p-0.5 transition-colors duration-200 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary2/90 focus-visible:ring-offset-2 focus-visible:ring-offset-black90 ${
                                popupEnabled
                                    ? "justify-end border-primary2 bg-primary2"
                                    : "justify-start border-black80 bg-black70"
                            }`}
                        >
                            <span
                                className="pointer-events-none h-5 w-5 rounded-full bg-white shadow"
                                aria-hidden
                            />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default DeveloperNavLogsPanel;
